/**
 * Iter 67fa — Vendor-branded micro-site + embeddable widget.
 *
 * Two surfaces share this component via `variant`:
 *   /v/:slug             → full-page micro-site (with Header + Footer)
 *   /embed/v/:slug       → iframe-optimized layout (no Header/Footer, scopes
 *                          the brand color CSS to the embed root)
 *
 * Both call `GET /api/public/vendors/by-slug/{slug}` which returns the vendor
 * card + their active cars in one round-trip. Clicking a car links to the
 * existing `/cars/:id` detail page where the existing booking widget already
 * does the full booking + payment flow. When the booking is created from a
 * micro-site/embed, the existing CarDetail page stamps `source_vendor_slug`
 * on the booking via localStorage (set in `useEffect` below).
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { Loader2, AlertCircle, MapPin, Clock, ShieldCheck, Star, Quote, Lock, BadgeCheck, Car as CarIcon, ExternalLink } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CarCard from "@/components/CarCard";

const LOCALSTORAGE_KEY = "rentalcar:source_vendor_slug";

function _hex_to_rgb(hex) {
  let h = (hex || "").replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const n = parseInt(h, 16) || 0;
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function _brand_css(color) {
  // Build a tiny CSS scope using the vendor's hex color. We override the
  // primary CTA color + accent bar inside this section only — does not leak
  // to <Header />.
  const [r, g, b] = _hex_to_rgb(color || "#1D4ED8");
  return {
    "--brand": color || "#1D4ED8",
    "--brand-soft": `rgba(${r}, ${g}, ${b}, 0.08)`,
    "--brand-soft-border": `rgba(${r}, ${g}, ${b}, 0.20)`,
  };
}

function HeroHeader({ vendor, rating }) {
  const logo = vendor.business_logo
    ? (vendor.business_logo.startsWith("http") ? vendor.business_logo : fileUrl(vendor.business_logo))
    : null;
  const cityLine = [vendor.business_city, vendor.business_country].filter(Boolean).join(", ");
  return (
    <section
      className="rounded-2xl p-6 sm:p-10 shadow-md border"
      style={{
        background: "linear-gradient(135deg, var(--brand-soft), white)",
        borderColor: "var(--brand-soft-border)",
      }}
      data-testid="microsite-hero"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-5">
        <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-2xl bg-white border shadow flex items-center justify-center overflow-hidden flex-shrink-0">
          {logo ? (
            <img src={logo} alt={vendor.business_name} className="h-full w-full object-contain" data-testid="microsite-logo" />
          ) : (
            <CarIcon className="h-10 w-10" style={{ color: "var(--brand)" }} />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold" style={{ color: "var(--brand)" }}>
            <ShieldCheck className="h-3 w-3" /> Trusted RentalCar.al host
          </div>
          <h1 className="text-2xl sm:text-4xl font-bold mt-1.5 break-words" data-testid="microsite-vendor-name">
            {vendor.business_name}
          </h1>
          {cityLine && (
            <div className="text-sm text-neutral-600 mt-1 flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" /> {cityLine}
            </div>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {/* Iter 74b — review rating (Google preferred, internal fallback). */}
            {rating?.rating != null && (
              <span
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 text-[11px] font-semibold"
                data-testid="microsite-rating"
                title={`${rating.rating.toFixed(1)} average from ${rating.total || 0} reviews`}
              >
                <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                {rating.rating.toFixed(1)}
                {rating.total ? (
                  <span className="text-amber-700/80 font-medium">· {rating.total} reviews</span>
                ) : null}
              </span>
            )}
            {vendor.business_24h ? (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[11px] font-semibold">
                <Clock className="h-3 w-3" /> Open 24/7
              </span>
            ) : (vendor.business_open_time && vendor.business_close_time) ? (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-sky-50 text-sky-700 border border-sky-200 text-[11px] font-semibold">
                <Clock className="h-3 w-3" /> {vendor.business_open_time}–{vendor.business_close_time}
              </span>
            ) : null}
            {/* Iter 74f — WhatsApp / email / phone removed entirely from the
                public microsite. Direct contact opens up on the customer
                dashboard once a booking is confirmed. */}
          </div>
        </div>
        {/* Iter 74f — hero right side used to hold a "Chat on WhatsApp" CTA.
            Removed so pre-booking visitors go through the reservation flow
            first; contact channels appear on their dashboard afterwards. */}
      </div>
      <div
        className="mt-4 pt-4 border-t border-neutral-100 flex items-center gap-1.5 text-[11px] text-neutral-500"
        data-testid="microsite-contact-note"
      >
        <Lock className="h-3 w-3" />
        Message the host directly from your dashboard once your booking is confirmed.
      </div>
    </section>
  );
}


function FeaturedReviews({ reviews }) {
  if (!reviews || reviews.length === 0) return null;
  return (
    <section
      className="rounded-2xl border border-neutral-200 bg-white p-5 sm:p-7 shadow-sm"
      data-testid="microsite-featured-reviews"
    >
      <div className="flex items-center gap-2 mb-4">
        <Star className="h-4 w-4 fill-amber-500 text-amber-500" />
        <h2 className="text-sm sm:text-base font-semibold text-neutral-800 uppercase tracking-wider">
          What guests say
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {reviews.map((r) => (
          <article
            key={r.id}
            className="relative rounded-2xl bg-gradient-to-br from-neutral-50 to-white border border-neutral-200 p-4 flex flex-col"
            data-testid={`featured-review-${r.id}`}
          >
            <Quote className="absolute top-3 right-3 h-6 w-6 text-neutral-200" />
            <div className="flex items-center gap-1 mb-2">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`h-3.5 w-3.5 ${
                    i < r.rating ? "fill-amber-400 text-amber-400" : "text-neutral-300"
                  }`}
                />
              ))}
            </div>
            <p className="text-sm text-neutral-700 line-clamp-4 flex-1">{r.comment}</p>
            <div className="mt-3 pt-3 border-t border-neutral-100 text-xs">
              <div className="font-medium text-neutral-800 flex items-center gap-1.5 flex-wrap">
                {r.user_name || "Guest"}
                {r.is_verified && (
                  <span
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-semibold"
                    title="This guest actually rented and paid — booking verified by RentalCar.al"
                    data-testid={`featured-review-verified-${r.id}`}
                  >
                    <BadgeCheck className="h-3 w-3" />
                    Verified guest
                  </span>
                )}
              </div>
              {r.car_label && (
                <div className="text-neutral-500 truncate">{r.car_label}</div>
              )}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

export default function VendorMicrosite({ variant = "microsite" }) {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [rating, setRating] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(`/public/vendors/by-slug/${encodeURIComponent(slug)}`);
        if (!active) return;
        setData(res.data);
        // Stamp the source vendor slug so the booking flow can attribute the
        // booking back to the widget when checkout runs (see CarDetail.jsx).
        // Iter 74l — we intentionally NO LONGER stamp `source_vendor_id` /
        // `source_vendor_name` here. Those two keys were the "sticky" hooks
        // that made the Header's "Browse Cars" link keep filtering to this
        // vendor after the customer left the microsite. The user asked for
        // the opposite: leaving the vendor page must drop them back onto the
        // full multi-vendor listing. Only the analytics-only slug is kept.
        try {
          localStorage.setItem(LOCALSTORAGE_KEY, slug);
        } catch (_e) { /* private-mode Safari */ }
        // Iter 74b — fetch Google review rating (non-blocking). If the vendor
        // has a google_place_id and cached reviews, we surface the star chip
        // in the hero. Falls back to the platform's internal rating (weighted
        // average of the vendor's car ratings) when Google returns nothing.
        if (res.data?.internal_rating) {
          setRating(res.data.internal_rating);
        }
        if (res.data?.vendor?.id) {
          api.get(`/vendors/${res.data.vendor.id}/google-reviews`).then((r) => {
            if (!active) return;
            const rt = r.data?.rating;
            if (typeof rt === "number" && rt > 0) {
              // Google trumps the internal aggregate because it usually has
              // a much larger sample and matches what renters see off-site.
              setRating({ rating: rt, total: r.data?.total || 0, source: "google" });
            }
          }).catch(() => {});
        }
      } catch (e) {
        setError(e?.response?.data?.detail || "Vendor not found");
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      active = false;
      // Iter 74l — drop the vendor-scoped state from memory on unmount so
      // React reclaims it and a next visit fetches fresh. Also clear any
      // leftover Header-scoping keys (from an earlier session that ran the
      // Iter-69 code) so returning users land on the multi-vendor listing.
      setData(null);
      setRating(null);
      try {
        const hadStickyKeys =
          localStorage.getItem("rentalcar:source_vendor_id") ||
          localStorage.getItem("rentalcar:source_vendor_name");
        localStorage.removeItem("rentalcar:source_vendor_id");
        localStorage.removeItem("rentalcar:source_vendor_name");
        localStorage.removeItem(LOCALSTORAGE_KEY);
        if (hadStickyKeys) {
          window.dispatchEvent(new CustomEvent("rentalcar:source-vendor-changed"));
        }
      } catch (_e) { /* private-mode Safari */ }
    };
  }, [slug]);

  const brandStyle = useMemo(
    () => _brand_css(data?.vendor?.brand_color),
    [data?.vendor?.brand_color]
  );

  const isEmbed = variant === "embed";

  const main = (
    <main
      className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${isEmbed ? "py-4" : "py-8"}`}
      style={brandStyle}
      data-testid="vendor-microsite-root"
      data-variant={variant}
    >
      {loading && (
        <div className="flex items-center justify-center py-24 text-neutral-500" data-testid="microsite-loading">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading vendor…
        </div>
      )}

      {!loading && error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center" data-testid="microsite-error">
          <AlertCircle className="h-8 w-8 text-rose-600 mx-auto mb-2" />
          <div className="font-semibold text-rose-900">{error}</div>
          <div className="text-sm text-rose-700 mt-1">Check the link or browse all cars on RentalCar.al.</div>
          {!isEmbed && (
            <Link to="/cars" className="inline-block mt-4 px-4 py-2 rounded-full bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700">
              Browse all cars
            </Link>
          )}
        </div>
      )}

      {!loading && !error && data && (
        <div className="space-y-8">
          <HeroHeader vendor={data.vendor} rating={rating} />

          <FeaturedReviews reviews={data.featured_reviews} />

          <section data-testid="microsite-cars-section">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="text-xl sm:text-2xl font-bold">
                {data.cars_count > 0
                  ? `${data.cars_count} car${data.cars_count === 1 ? "" : "s"} available`
                  : "No cars listed yet"}
              </h2>
              {!isEmbed && data.cars_count > 0 && (
                <Link
                  to={`/cars?vendor_id=${data.vendor.id}`}
                  className="text-sm font-semibold underline-offset-2 hover:underline"
                  style={{ color: "var(--brand)" }}
                >
                  View on RentalCar.al →
                </Link>
              )}
            </div>

            {data.cars_count === 0 ? (
              <div className="rounded-2xl border border-dashed p-12 text-center text-neutral-500" data-testid="microsite-no-cars">
                This host hasn't published any cars yet.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="microsite-cars-grid">
                {data.cars.map((car, idx) => (
                  <CarCard key={car.id} car={car} index={idx} />
                ))}
              </div>
            )}
          </section>

          {/* Compliance footer — required for the embed too so users always know who's hosting the inventory. */}
          <section
            className="mt-8 pt-6 border-t text-xs text-neutral-500 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
            data-testid="microsite-footer-strip"
          >
            <div>
              Bookings are processed by <a href="https://rentalcar.al" className="font-semibold text-neutral-700 hover:underline">RentalCar.al</a>
              {data.vendor.vat_number ? <> · VAT/NIPT <span className="font-mono">{data.vendor.vat_number}</span></> : null}
            </div>
            {isEmbed && (
              <a
                href={`/v/${data.vendor.public_slug}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 font-semibold hover:underline"
                style={{ color: "var(--brand)" }}
                data-testid="microsite-open-fullsite"
              >
                Open full site <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </section>
        </div>
      )}
    </main>
  );

  // Embed variant: bare layout, no Header/Footer, lets iframe size match content.
  if (isEmbed) {
    return (
      <div className="min-h-screen bg-white" data-testid="vendor-embed-root">
        {main}
      </div>
    );
  }

  // Full micro-site variant: standard chrome.
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="flex-1">{main}</div>
      <Footer />
    </div>
  );
}
